import React, { useState, useEffect, useRef } from 'react';
import { Zap, Brain, Coffee, Moon, Play, Star, Bookmark, Film } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Movie } from '../types';
import { fetchDiscover, getPosterSrcSet } from '../services/tmdb';
import { generateSlug } from '../utils/slug';
import { isInWatchlist, toggleWatchlist } from '../utils/watchlist';

interface MoodOption {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  params: string;
  accentColor: string;
  badge: string;
}

const MOODS: MoodOption[] = [
  {
    id: 'adrenaline',
    name: 'High Adrenaline',
    description: 'Explosive action & fast-paced thrillers',
    icon: <Zap className="w-5 h-5 text-amber-400" />,
    params: 'with_genres=28,53&sort_by=popularity.desc',
    accentColor: 'from-amber-500/20 to-red-600/20',
    badge: 'Action & Thrillers'
  },
  {
    id: 'mind-bending',
    name: 'Mind Bending',
    description: 'Psychological twists & sci-fi mysteries',
    icon: <Brain className="w-5 h-5 text-purple-400" />,
    params: 'with_genres=878,9648&sort_by=vote_average.desc&vote_count.gte=1000',
    accentColor: 'from-purple-500/20 to-indigo-600/20',
    badge: 'Sci-Fi & Mystery'
  },
  {
    id: 'cozy-chill',
    name: 'Cozy & Chill',
    description: 'Heartwarming family & romantic picks',
    icon: <Coffee className="w-5 h-5 text-emerald-400" />,
    params: 'with_genres=10751,10749,35&sort_by=popularity.desc',
    accentColor: 'from-emerald-500/20 to-teal-600/20',
    badge: 'Feel-Good & Comedy'
  },
  {
    id: 'dark-gritty',
    name: 'Dark & Gritty',
    description: 'Crime sagas & atmospheric horror',
    icon: <Moon className="w-5 h-5 text-blue-400" />,
    params: 'with_genres=80,27&sort_by=popularity.desc',
    accentColor: 'from-blue-500/20 to-slate-800/40',
    badge: 'Crime & Horror'
  }
];

export const VibeDiscovery: React.FC = () => {
  const [activeMood, setActiveMood] = useState<MoodOption>(MOODS[0]);
  const [movies, setMovies] = useState<Movie[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [savedIds, setSavedIds] = useState<string[]>([]);
  const [isInView, setIsInView] = useState<boolean>(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setIsInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '350px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isInView) return;
    let isMounted = true;
    setLoading(true);

    fetchDiscover('movie', activeMood.params)
      .then((res) => {
        if (isMounted) {
          setMovies(res.slice(0, 8));
          setLoading(false);
        }
      })
      .catch(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [activeMood, isInView]);

  const handleToggleBookmark = (e: React.MouseEvent, movie: Movie) => {
    e.preventDefault();
    e.stopPropagation();
    const isAdded = toggleWatchlist(movie);
    if (isAdded) {
      setSavedIds((prev) => [...prev, movie.id]);
    } else {
      setSavedIds((prev) => prev.filter((id) => id !== movie.id));
    }
  };

  return (
    <section
      ref={sectionRef}
      style={{ contentVisibility: 'auto', containIntrinsicSize: '1200px 460px' }}
      className="relative px-4 md:px-12 py-10 space-y-8 max-w-[1600px] mx-auto overflow-hidden"
    >
      {/* Background Ambient Glow Accent */}
      <div className={`absolute inset-0 bg-gradient-to-r ${activeMood.accentColor} opacity-30 blur-3xl pointer-events-none transition-all duration-700`} />

      {/* Title */}
      <div className="relative z-10 space-y-1 border-b border-white/10 pb-4">
        <span className="text-[11px] font-black uppercase tracking-widest text-red-400 font-mono flex items-center gap-2">
          <Film className="w-3.5 h-3.5" /> Curated Cinema Selection
        </span>
        <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
          What Are You in the Mood For?
        </h2>
        <p className="text-xs text-zinc-300">
          Select one of the 4 cinema moods to instant-match movies for your current vibe.
        </p>
      </div>

      {/* 4 Mood Option Cards */}
      <div className="relative z-10 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {MOODS.map((mood) => {
          const isActive = activeMood.id === mood.id;
          return (
            <button
              type="button"
              key={mood.id}
              onClick={() => setActiveMood(mood)}
              aria-pressed={isActive}
              className={`relative flex flex-col p-4 rounded-2xl border text-left transition-all duration-300 cursor-pointer overflow-hidden ${
                isActive
                  ? 'bg-zinc-900 border-red-500 text-white shadow-xl shadow-red-600/20 ring-1 ring-red-500/50 scale-[1.02]'
                  : 'bg-zinc-900/70 border-white/10 text-zinc-300 hover:text-white hover:bg-zinc-900 hover:border-white/20'
              }`}
            >
              <div className="flex items-center justify-between w-full mb-2">
                <div className={`p-2 rounded-xl ${isActive ? 'bg-red-600/25 text-red-400' : 'bg-zinc-800 text-zinc-300'}`}>
                  {mood.icon}
                </div>
                <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full border ${
                  isActive ? 'bg-red-600/25 border-red-400/50 text-red-200' : 'bg-zinc-800 border-white/10 text-zinc-300'
                }`}>
                  {mood.badge}
                </span>
              </div>
              <h3 className="text-sm font-extrabold text-white tracking-tight mb-1">{mood.name}</h3>
              <p className="text-[11px] text-zinc-300 line-clamp-1">{mood.description}</p>
            </button>
          );
        })}
      </div>

      {/* Grid Display (8 movies for active mood) */}
      <div className="relative z-10">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 animate-pulse">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[2/3] bg-zinc-900 rounded-2xl border border-white/5" />
            ))}
          </div>
        ) : (
          <div
            key={activeMood.id}
            className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 sm:gap-5 transition-opacity duration-300"
          >
            {movies.map((movie) => {
              const isSaved = savedIds.includes(movie.id) || isInWatchlist(movie.id);
              const movieUrl = `${generateSlug(movie)}?type=${movie.type}`;

              return (
                <div
                  key={movie.id}
                  style={{ contentVisibility: 'auto', containIntrinsicSize: '200px 300px' }}
                  className="group relative bg-zinc-900/70 rounded-2xl overflow-hidden border border-white/10 hover:border-red-600/60 transition-all duration-300 flex flex-col shadow-xl hover:shadow-red-600/20 hover:-translate-y-1.5"
                >
                  {/* Bookmark Toggle (sibling to Link so interactive controls are not nested) */}
                  <button
                    type="button"
                    onClick={(e) => handleToggleBookmark(e, movie)}
                    className={`absolute top-2 right-2 p-2.5 min-w-[36px] min-h-[36px] flex items-center justify-center rounded-full backdrop-blur-md transition-all shadow-lg z-20 cursor-pointer ${
                      isSaved
                        ? 'bg-red-600 text-white'
                        : 'bg-black/70 text-white hover:bg-red-600'
                    }`}
                    aria-label={isSaved ? `Remove ${movie.title} from watchlist` : `Add ${movie.title} to watchlist`}
                  >
                    <Bookmark className={`w-3.5 h-3.5 ${isSaved ? 'fill-current' : ''}`} />
                  </button>

                  <Link to={movieUrl} className="flex flex-col flex-1 focus:outline-none">
                    <div className="relative aspect-[2/3] w-full bg-zinc-950 overflow-hidden">
                      <img
                        src={movie.thumbnail || `https://placehold.co/342x513/000000/e11d48?text=${encodeURIComponent(movie.title)}`}
                        srcSet={getPosterSrcSet(movie.thumbnail)}
                        sizes="(max-width: 640px) 154px, 185px"
                        alt={movie.title}
                        width={342}
                        height={513}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/20 to-black/40 opacity-70 group-hover:opacity-90 transition-opacity" />

                      {/* Rating Badge */}
                      {movie.rating && (
                        <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-black/85 backdrop-blur-md px-2 py-0.5 rounded-md text-[10px] font-black text-yellow-400 border border-white/10">
                          <Star className="w-3 h-3 fill-current" /> {movie.rating}
                        </div>
                      )}

                      {/* Hover Play Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40 backdrop-blur-[2px]">
                        <span className="p-3 bg-red-600 text-white rounded-full shadow-2xl scale-90 group-hover:scale-100 transition-transform">
                          <Play className="w-4 h-4 fill-current" />
                        </span>
                      </div>
                    </div>

                    <div className="p-2.5 space-y-0.5 bg-zinc-950/90 border-t border-white/5">
                      <h3 className="text-xs font-extrabold text-white truncate group-hover:text-red-400 transition-colors">
                        {movie.title}
                      </h3>
                      <p className="text-[10px] text-zinc-300 font-medium truncate">{movie.category || movie.year || 'Cinema'}</p>
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};


